import {
  Autocomplete,
  ListItemProps,
  MenuItem,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { camelCase, get, isObject, isString } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { InputComponent } from '..';
import { normalizeSize } from '../helpers';

export type InputSelectOption =
  | {
      value: string | number;
      label: string;
      disabled?: boolean;
      group?: string;
    }
  | string
  | number;

export interface InputSelectProps extends InputComponent {
  options?: InputSelectOption[];
  autoSelectFirstOption?: boolean;
  autoFocus?: boolean;
  sx?: TextFieldProps['sx'];
}

export function InputSelect({
  label,
  options,
  disabled,
  hint,
  required,
  nameOverride,
  hidden,
  size: propSize,
  readOnly = false,
  autoSelectFirstOption = true,
  autoFocus = false,
  sx = {},
}: InputSelectProps) {
  const { values, setFieldValue, errors, validateField } =
    useFormikContext<Record<string, string>>();

  const validate = useCallback(
    (value: string | number | undefined) => {
      if (required && (!value || value === undefined)) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required, values],
  );

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  const size = normalizeSize(propSize);

  const inputRef = useRef<HTMLInputElement>(null);

  // The bare `autoFocus` attribute fires before MUI's Dialog FocusTrap settles
  // focus after its open transition, so it doesn't stick. Focus on the next
  // frame instead, once the trap has finished.
  useEffect(() => {
    if (!autoFocus) return undefined;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [autoFocus]);

  function renderOption(
    option: InputSelectOption,
    props: ListItemProps,
  ): React.ReactElement {
    return isObject(option) ? (
      <MenuItem value={option.value} disabled={option.disabled} {...props}>
        {option.label}
      </MenuItem>
    ) : (
      <MenuItem
        value={isString(option) ? camelCase(option) : option}
        {...props}
      >
        {option}
      </MenuItem>
    );
  }

  // Read through a ref so the guard below always sees the current value
  // without putting `values` in the effect's dep array (which would make the
  // effect fire on every keystroke anywhere in the form).
  const valuesRef = useRef(values);
  valuesRef.current = values;

  // Auto-select is a convenience, not a state machine: only write when the
  // field isn't already holding the value. Callers routinely build `options`
  // inline, so this effect re-fires on every render; an unconditional
  // setFieldValue there loops forever (render -> new array identity -> write
  // -> render) and takes the tab down with it.
  useEffect(() => {
    if (!autoSelectFirstOption || options?.length !== 1) return;
    const opt = options[0];
    const next = typeof opt === 'object' ? opt.value : opt;
    if (get(valuesRef.current, name) === next) return;
    setFieldValue(name, next);
  }, [autoSelectFirstOption, options, name, setFieldValue]);

  const selectedOption = useMemo(
    () =>
      options?.find((opt) => {
        if (isObject(opt)) {
          return opt.value === get(values, name);
        }
        return opt === get(values, name);
      }),
    [name, values, options],
  );

  // Group options by their group property if they have one
  const groupBy = useCallback((option: InputSelectOption) => {
    if (isObject(option) && option.group) {
      return option.group;
    }
    return '';
  }, []);

  return (
    <Field name={name} validate={validate}>
      {() => (
        <Autocomplete<InputSelectOption, false, boolean, false>
          disabled={disabled || readOnly}
          hidden={hidden}
          fullWidth
          options={options ?? []}
          disableClearable={required}
          value={selectedOption ?? null}
          sx={{
            ...sx,
            '& .MuiAutocomplete-groupLabel': {
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.08333em',
              color: 'text.secondary',
              backgroundColor: 'transparent',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            },
          }}
          groupBy={groupBy}
          renderOption={(props: ListItemProps, option: InputSelectOption) =>
            renderOption(option, props)
          }
          getOptionLabel={(option: InputSelectOption) =>
            isObject(option) ? option.label : String(option)
          }
          renderInput={(params: TextFieldProps) => (
            <TextField
              {...params}
              inputRef={inputRef}
              label={label}
              size={size as TextFieldProps['size']}
              required={required && !readOnly}
              error={!!error}
              helperText={error || hint}
              autoFocus={autoFocus}
            />
          )}
          onChange={(
            _evt: React.SyntheticEvent,
            value: InputSelectOption | null,
          ) => {
            if (!readOnly) {
              setFieldValue(name, isObject(value) ? value.value : value);
            }
          }}
        />
      )}
    </Field>
  );
}
